import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { message, Input, Button as AntButton } from 'antd';
import { SaveIcon, UserIcon } from '@heroicons/react/outline';
import { colors, typography, spacing } from '../../design-system';
import { ProfileImageUploadSimple } from '../../common/ProfileImageUploadSimple';
import { userService, User } from '../../services/userService';

const ProfileContainer = styled.div`
  padding: ${spacing[6]};
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[6]};
  margin-bottom: ${spacing[8]};
  padding-bottom: ${spacing[6]};
  border-bottom: 1px solid ${colors.border.secondary};
`;

const ProfileImageSection = styled.div`
  flex-shrink: 0;
`;

const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProfileTitle = styled.h3`
  font-family: ${typography.fontFamilies.sans};
  font-size: ${typography.fontSizes.lg};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  margin: 0 0 ${spacing[2]} 0;
`;

const ProfileDescription = styled.p`
  font-family: ${typography.fontFamilies.sans};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
  margin: 0 0 ${spacing[4]} 0;
  line-height: 1.5;
`;

const UserEmail = styled.div`
  font-family: ${typography.fontFamilies.sans};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.tertiary};
  font-weight: ${typography.fontWeights.medium};
`;

const ProfileForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[5]};
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const Label = styled.label`
  font-family: ${typography.fontFamilies.sans};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.primary};
`;

const StyledInput = styled(Input)`
  font-family: ${typography.fontFamilies.sans};
  font-size: ${typography.fontSizes.sm};
  border-radius: 8px;
  border: 1px solid ${colors.border.primary};
  
  &:focus, &:hover {
    border-color: ${colors.brand.primary};
    box-shadow: 0 0 0 2px ${colors.brand.primarySurface};
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing[3]};
  margin-top: ${spacing[6]};
  padding-top: ${spacing[6]};
  border-top: 1px solid ${colors.border.secondary};
`;

const SaveButton = styled(AntButton)`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  border-radius: 8px;
  border: 1px solid ${colors.brand.primary};
  background: ${colors.brand.primary};
  color: white;
  font-weight: ${typography.fontWeights.medium};
  
  &:hover {
    border-color: ${colors.brand.primaryHover};
    background: ${colors.brand.primaryHover};
    color: white;
  }
  
  &:disabled {
    border-color: ${colors.border.secondary};
    background: ${colors.background.secondary};
    color: ${colors.text.disabled};
  }
`;

interface ProfileSectionProps {
  onProfileUpdated?: () => void;
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({
  onProfileUpdated
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [displayName, setDisplayName] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>();

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const currentUser = await userService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        setDisplayName(currentUser.name || '');
        setProfileImageUrl(currentUser.profile_image_url);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
      // message.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpdate = (imageUrl: string) => {
    setProfileImageUrl(imageUrl);
    // message.success('Profile image updated successfully');
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      // Note: Currently we only support image uploads through the ProfileImageUpload component
      // Display name updates would require additional Cognito attribute updates
      
      // For now, just refresh user data to show any changes
      await loadUserData();
      
      if (onProfileUpdated) {
        onProfileUpdated();
      }
      
      // message.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      // message.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: spacing[8] }}>
          Loading profile...
        </div>
      </ProfileContainer>
    );
  }

  if (!user) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: spacing[8] }}>
          Failed to load profile data
        </div>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <ProfileHeader>
        <ProfileImageSection>
          <ProfileImageUploadSimple
            currentImageUrl={profileImageUrl}
            onImageUpdate={handleImageUpdate}
            size={80}
            editable={true}
          />
        </ProfileImageSection>
        
        <ProfileInfo>
          <ProfileTitle>Profile Settings</ProfileTitle>
          <ProfileDescription>
            Update your profile image and personal information. Changes will be reflected across the platform.
          </ProfileDescription>
          <UserEmail>{user.email}</UserEmail>
        </ProfileInfo>
      </ProfileHeader>

      <ProfileForm>
        <FormRow>
          <Label>Display Name</Label>
          <StyledInput
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your display name"
            disabled={true} // Disabled for now - would need additional Cognito setup
          />
          <div style={{ 
            fontSize: typography.fontSizes.xs, 
            color: colors.text.tertiary,
            marginTop: spacing[1]
          }}>
            Display name updates will be available in a future update
          </div>
        </FormRow>

        <FormRow>
          <Label>Email Address</Label>
          <StyledInput
            value={user.email}
            disabled={true}
            placeholder="Email address"
          />
          <div style={{ 
            fontSize: typography.fontSizes.xs, 
            color: colors.text.tertiary,
            marginTop: spacing[1]
          }}>
            Email address cannot be changed from this interface
          </div>
        </FormRow>

        <FormRow>
          <Label>Organization</Label>
          <StyledInput
            value={user.organization_name || ''}
            disabled={true}
            placeholder="Organization name"
          />
          <div style={{ 
            fontSize: typography.fontSizes.xs, 
            color: colors.text.tertiary,
            marginTop: spacing[1]
          }}>
            Organization settings are managed in the Organization section
          </div>
        </FormRow>
      </ProfileForm>

      <FormActions>
        <SaveButton
          type="primary"
          loading={saving}
          disabled={saving}
          onClick={handleSave}
        >
          <SaveIcon style={{ width: '14px', height: '14px' }} />
          Save Profile
        </SaveButton>
      </FormActions>
    </ProfileContainer>
  );
};
